/**
 * The space-slot card's single content row (W2 §rotating-row). It rotates
 * between the signals PRESENT for the event — helpable first (highest intent),
 * then who's going, then the posts count — dwelling `dwellMs` on each and
 * cross-fading between them.
 *
 * ONE shared tick (no per-card timer): the rotation index is derived purely
 * from `rotateNowMs`, a single host `useNow(slotRotateDwellMs)` clock threaded
 * to every card — `floor(elapsed / dwellMs) % signalCount`, where `elapsed` is
 * measured from a per-appearance anchor off that same clock. So K cards on a
 * space page rotate in lockstep off one interval, and the feed's tick isolation
 * is untouched (this leaf mounts no timer of its own).
 *
 * Motion is v1's §4.1 blur-masked swap (the `SpaceIndicator` "at {event}"
 * cross-fade): opacity + blur(4px→0) + scale(0.97→1) at 200ms `ease-swap`, the
 * blur masking the crossfade seam. v1 locked "the pulse is the layer's only
 * looping animation"; Dexter amended that for this rotation (see `LiveBadge` /
 * `LiveStateChip`) — it stays within the same motion language.
 *
 * Auto-rotation runs FOR THE LIFE OF THE MOUNT (Dexter's product ruling
 * 2026-07-23: "make the text change keep going forever"). WCAG 2.2.2
 * (auto-updating content) is met by the retained mitigations, not a cycle bound:
 *   - `prefers-reduced-motion` → NO rotation; the lead signal shows statically.
 *   - Rotation PAUSES while the card is hovered or contains focus (the card root
 *     drives `LiveCardPauseContext`); paused time is folded out of `elapsed`, so
 *     a pause holds the exact frame — a reachable stop for pointer/keyboard users.
 *   - NOT an `aria-live` region: the visible signals are `aria-hidden`; one
 *     `sr-only` line carries EVERY present signal statically, so assistive tech
 *     hears the whole story at once, once — never a per-swap announcement.
 *   On touch (the primary platform) the hover/focus pause is unreachable, so the
 *   auto-rotation is perpetual there — an accepted product trade for the
 *   glanceable rotating cue, with reduced-motion + static AT exposure as the
 *   standing mitigations. The host's idle-clock gate still disarms the shared
 *   clock entirely when no mounted cell can rotate.
 *   - Degenerate: exactly one signal → a static row; zero signals with a
 *     non-empty `fallback` → a static muted date; zero signals with an empty
 *     `fallback` → the row is omitted (the chip carries the date, W2.7).
 *
 * Posts (W2.7): the posts count is now the THIRD rotation signal (`postsSignal`),
 * a full-width muted frame in the cycle — it was an inline `· {m} posts` tail
 * that truncated the rotating signal beside it.
 */
import { use, useEffect, useState, type ReactNode } from 'react';
import { cn } from '@lib/utils';
import { useLatestRef } from '@hooks/useLatestRef';
import { usePrefersReducedMotion } from '@hooks/usePrefersReducedMotion';
import { LiveCardPauseContext } from './liveCardPause';
import { SWAP_MOTION } from './liveMotion';

/**
 * One rotating signal (W2.6, Figma 6940:18935/6940:18338): the visible text plus
 * an optional leading visual (helpable icon / who's-going facepile) and the
 * count-bearing sr-only phrase. `text` may drop a count the `leading` visual
 * carries (the facepile's `+N`); `srText` always states it in full.
 */
export interface SlotContentSignal {
  /** Stable identity — the React key AND the appearance-set membership token. */
  key: string;
  /** Visible text (aria-hidden; the leading visual sits beside it). Teal by default. */
  text: string;
  /** Full sr-only phrase — carries any count the visuals compress (§0 a11y). */
  srText: string;
  /** Optional leading visual (helpable icon / facepile), aria-hidden decoration. */
  leading?: ReactNode;
  /** Muted meta signal (grey, not teal) — the posts count, which is meta, not a teal cue. */
  muted?: boolean;
}

export interface LiveSlotContentRowProps {
  /** Signal 1 (`composeSlotHelpableSignal` + the helpable icon), or null when absent. */
  helpableSignal: SlotContentSignal | null;
  /** Signal 2 (`composeSlotGoingSignal` + the facepile), or null when absent. */
  goingSignal: SlotContentSignal | null;
  /**
   * Signal 3 (W2.7): the muted posts count (`composeRecapPosts` → "8 posts"), a
   * FULL-WIDTH rotation frame (no longer an inline tail — the tail truncated the
   * signal beside it). Live cells only; null when absent (soon/upcoming, or 0).
   */
  postsSignal: SlotContentSignal | null;
  /**
   * Static row when NO signal is present. Empty string ('') omits the row
   * entirely (W2.7: the card chip now carries the date, so a zero-signal card
   * needn't repeat it). A non-empty value renders statically as the muted lead.
   */
  fallback: string;
  /** Shared dwell-clock epoch ms (`useNow(slotRotateDwellMs)` at the host). */
  rotateNowMs: number;
  /** Dwell per signal (`LIVE_EVENTS_CONFIG.slotRotateDwellMs`). */
  dwellMs: number;
  /** Host callback: whether this row actually rotates (>1 present signal).
   * The slot arms its shared 6s clock from this instead of re-deriving an
   * approximation (the helpable "potential" over-armed a perpetual clock for
   * cells whose real count was 0 — design §5). */
  onRotatableChange?: (rotatable: boolean) => void;
}

export function LiveSlotContentRow({
  helpableSignal,
  goingSignal,
  postsSignal,
  fallback,
  rotateNowMs,
  dwellMs,
  onRotatableChange,
}: LiveSlotContentRowProps) {
  const reduceMotion = usePrefersReducedMotion();
  const paused = use(LiveCardPauseContext);

  // Present signals, highest-intent first (helpable, then who's going, then the
  // posts count). The present signals are teal (posts muted) with a leading
  // visual; the zero-signal fallback is a muted, leading-less date.
  const present = [helpableSignal, goingSignal, postsSignal].filter(
    (signal): signal is SlotContentSignal => signal !== null,
  );
  const onFallback = present.length === 0;

  // Zero signals AND no fallback → omit the row (W2.7: the chip carries the date,
  // so a zero-signal card needn't repeat it). A non-empty fallback still renders.
  // Placed before the rotation hook is READ, but the hook itself still runs
  // unconditionally below via the early sentinel — see the guard: we return only
  // AFTER computing nothing state-changing, keeping hook order stable.
  const signals: SlotContentSignal[] = onFallback
    ? [{ key: 'fallback', text: fallback, srText: fallback, muted: true }]
    : present;
  const count = signals.length;
  const rotates = count > 1 && !reduceMotion;

  // Report the row's ACTUAL rotate condition upward (>1 present signal) — the
  // host arms its shared 6s clock from this instead of a host-side approximation.
  const onRotatableChangeRef = useLatestRef(onRotatableChange);
  useEffect(() => {
    const notify = onRotatableChangeRef.current;
    notify?.(count > 1);
    return () => {
      notify?.(false);
    };
  }, [count, onRotatableChangeRef]);

  // Appearance identity = the present-signal SET. A new event / phase flip
  // remounts this row (the host keys the inner on `event.id:phase`), so within a
  // mount the only thing that can change is which signals are present (helpable
  // refetching in/out, posts resolving). A changed set is a FRESH appearance that
  // re-anchors the cycle at the lead; a mere tick or content-only refresh does not.
  const appearanceKey = `${helpableSignal !== null}:${goingSignal !== null}:${postsSignal !== null}`;

  // ── ACCEPTED DECISION (ADR) — perpetual auto-rotation, no cycle bound ──────────
  // The content row auto-rotates for the entire life of the mount; it does NOT park
  // on the lead signal after N cycles. This is a ratified PRODUCT decision (Dexter
  // 2026-07-23 "make the text change keep going forever", re-ratified 2026-07-24 over
  // the WCAG 2.2.2 review comment), NOT an oversight. WCAG 2.2.2 (Pause, Stop, Hide)
  // is met by the STANDING MITIGATIONS below rather than a cycle bound:
  //   1. `prefers-reduced-motion` → no rotation at all; the lead signal shows static.
  //   2. Hover/focus PAUSES the row (the card root drives `LiveCardPauseContext`) — a
  //      reachable stop for pointer + keyboard users; paused time is folded out of
  //      `elapsed`, so a pause holds the exact frame.
  //   3. Every present signal is exposed to assistive tech AT ONCE via the static
  //      sr-only line below (NOT an aria-live region), so AT hears the whole story
  //      once and never a per-swap announcement.
  // Accepted trade: on TOUCH (the primary platform) mitigation 2 is unreachable, so
  // the rotation is perpetual there — the glanceable rotating cue is judged worth it,
  // with 1 + 3 as the standing accessibility guarantees. See the twin ADR note on
  // `LIVE_EVENTS_CONFIG.slotRotateDwellMs`. Change this ONLY on a new Dexter ruling.
  // ───────────────────────────────────────────────────────────────────────────────
  // `elapsed` is measured off the SAME shared clock the index uses (`rotateNowMs −
  // anchor`); a pause shifts the anchor forward by the paused span, so paused time
  // never advances the index. Guarded render-phase setState (React's "store info from
  // previous renders" — each branch self-disables next render), so no effect and no
  // timer of its own. `pausePegMs` holds the clock value at pause-start; 0 is a valid
  // peg, hence the `=== null` guards.
  const [win, setWin] = useState<{ key: string; anchorMs: number; pausePegMs: number | null }>(
    () => ({
      key: appearanceKey,
      anchorMs: rotateNowMs,
      pausePegMs: paused && rotates ? rotateNowMs : null,
    }),
  );
  if (win.key !== appearanceKey) {
    // Fresh appearance: re-anchor now (peg immediately if we open paused).
    setWin({
      key: appearanceKey,
      anchorMs: rotateNowMs,
      pausePegMs: paused && rotates ? rotateNowMs : null,
    });
  } else if (paused && rotates && win.pausePegMs === null) {
    // Pause begins: remember where the clock was.
    setWin({ ...win, pausePegMs: rotateNowMs });
  } else if ((!paused || !rotates) && win.pausePegMs !== null) {
    // Resume: fold the paused span into the anchor so `elapsed` continues where
    // it left off (the index did not advance while paused).
    setWin({ ...win, anchorMs: win.anchorMs + (rotateNowMs - win.pausePegMs), pausePegMs: null });
  }

  const pausedNowMs = win.pausePegMs !== null ? rotateNowMs - win.pausePegMs : 0;
  const dwellsElapsed = Math.max(
    0,
    Math.floor((rotateNowMs - win.anchorMs - pausedNowMs) / dwellMs),
  );
  // Rotating → the shared-tick index, forever; otherwise the lead (index 0).
  const activeIndex = rotates ? dwellsElapsed % count : 0;

  const srLine = signals.map(signal => signal.srText).join('. ');

  // Present teal signals wear the Figma teal SemiBold (6940:18935/6940:18338); the
  // muted ones (posts, the zero-signal date fallback) stay the muted grey.
  const tone = (signal: SlotContentSignal) =>
    signal.muted ? 'font-normal text-mb-gray-text' : 'font-semibold text-mb-turquoise-700';

  // W2.7: a zero-signal card with no fallback omits the row entirely (the chip
  // carries the date). Returned AFTER the hook above so hook order stays stable.
  if (onFallback && !fallback) {
    return null;
  }

  return (
    <span className="flex min-w-0 flex-col">
      {/* min-h-6 reserves the tallest signal's height (the h-6 facepile / helpable
          icon), so a cross-fade between an icon row and a face row never nudges
          the card — the fixed floor replaces the old invisible height copy and
          never renders the facepile twice. */}
      <span className="relative min-h-6 min-w-0">
        {signals.map((signal, index) => (
          <span
            key={signal.key}
            aria-hidden="true"
            className={cn(
              'absolute inset-0 flex items-center gap-1 text-xs',
              tone(signal),
              SWAP_MOTION.transition,
              index === activeIndex ? SWAP_MOTION.shown : SWAP_MOTION.hidden,
            )}
          >
            {signal.leading ? (
              <span className="flex shrink-0 items-center">{signal.leading}</span>
            ) : null}
            <span className="min-w-0 truncate tabular-nums">{signal.text}</span>
          </span>
        ))}
      </span>
      {/* Every present signal, static, for assistive tech (not aria-live). */}
      <span className="sr-only">{srLine}</span>
    </span>
  );
}
